import type {
  Ticket, TicketMessage, TicketActivity, TicketFilter, TicketStatus, TicketPriority,
  SupportAgent, SharedInbox, SupportAttachmentRef, Page,
} from "@/types";
import { Endpoints, http } from "./http";

export const SupportApi = {
  listInboxes(): Promise<SharedInbox[]> {
    return http(Endpoints.support_inboxes);
  },
  listAgents(): Promise<SupportAgent[]> {
    return http(Endpoints.support_agents);
  },
  listTags(): Promise<string[]> {
    return http(Endpoints.support_tags);
  },

  list(filter: TicketFilter, cursor?: string): Promise<Page<Ticket>> {
    const u = new URL(Endpoints.support_tickets, window.location.origin);
    if (filter.inbox_id) u.searchParams.set("inbox", filter.inbox_id);
    if (filter.status)   u.searchParams.set("status", Array.isArray(filter.status) ? filter.status.join(",") : filter.status);
    if (filter.assigned_to) u.searchParams.set("assignee", filter.assigned_to);
    if (filter.priority) u.searchParams.set("priority", filter.priority);
    if (filter.tag)      u.searchParams.set("tag", filter.tag);
    if (filter.search)   u.searchParams.set("q", filter.search);
    if (cursor)          u.searchParams.set("cursor", cursor);
    return http(u.pathname + u.search);
  },

  get(id: string): Promise<Ticket> {
    return http(Endpoints.support_ticket(id));
  },
  messages(id: string): Promise<TicketMessage[]> {
    return http(Endpoints.support_messages(id));
  },
  activity(id: string): Promise<TicketActivity[]> {
    return http(Endpoints.support_activity(id));
  },

  // Mutations
  reply(
    ticket_id: string,
    body: {
      text: string;
      html?: string;
      attachment_ids?: string[];
      attachments?: SupportAttachmentRef[];
    },
  ): Promise<TicketMessage> {
    return http(Endpoints.support_messages(ticket_id), { method: "POST", body: JSON.stringify({ ...body, direction: "outbound" }) });
  },
  // Forward the whole report (every non-internal message + their attachments)
  // to an address the agent types in. `skipped` lists any attachment that
  // could not be included, so the UI can say so rather than implying the
  // recipient got the full evidence.
  forward(
    ticket_id: string,
    body: { to: string; note?: string },
  ): Promise<{ forwarded: boolean; to: string; messages: number; attachments: number; skipped: string[] }> {
    return http(Endpoints.support_forward(ticket_id), { method: "POST", body: JSON.stringify(body) });
  },
  addInternalNote(ticket_id: string, body: { text: string }): Promise<TicketMessage> {
    return http(Endpoints.support_messages(ticket_id), { method: "POST", body: JSON.stringify({ ...body, direction: "internal", is_internal_note: true }) });
  },

  // Upload a single file for use as an attachment on the next reply.
  // Mirrors JmapApi.uploadBlob but stores in R2 under support@ scope so
  // the agent doesn't need direct JMAP access to support@'s mailbox.
  // Returns an opaque ref the composer carries forward into reply().
  async uploadAttachment(file: File): Promise<SupportAttachmentRef> {
    const u = new URL("/api/support/attachments/upload", window.location.origin);
    u.searchParams.set("name", file.name);
    const res = await fetch(u.pathname + u.search, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type || "application/octet-stream", "Origin": window.location.origin },
      body: file,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/api/support/attachments/upload ${res.status}: ${text.slice(0, 200)}`);
    }
    return res.json();
  },

  // Download URL for a ticket attachment (id is the ticket_attachments row id,
  // returned in TicketMessage.attachments[].id). Browser hits this and the
  // Worker streams bytes from R2 or via the IMAP bridge as appropriate.
  attachmentUrl(attachment_id: string): string {
    return `/api/support/attachments/${encodeURIComponent(attachment_id)}/download`;
  },
  setStatus(id: string, status: TicketStatus): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ status }) });
  },
  setPriority(id: string, priority: TicketPriority): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ priority }) });
  },
  assign(id: string, user_id: string | null): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ assigned_to: user_id }) });
  },
  setInbox(id: string, inbox_id: string): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ inbox_id }) });
  },
  addTag(id: string, tag: string): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ tag_add: tag }) });
  },
  removeTag(id: string, tag: string): Promise<Ticket> {
    return http(Endpoints.support_ticket(id), { method: "PATCH", body: JSON.stringify({ tag_remove: tag }) });
  },
  delete(id: string): Promise<void> {
    return http(Endpoints.support_ticket(id), { method: "DELETE" });
  },
};
